<template>
  <li
    class="cart-item"
    :class="{ 'print' : printItem, 'digital' : !printItem, 'cart-photo-pack': isPhotoPack }"
  >
    <article>
      <!--displayed photos-->
      <ProductPreview
        class="pl-3 pr-1 md:pl-0 md:pr-0"
        :item="item"
      />

      <section class="product-details">
        <h2>{{ productName }}</h2>

        <h3 class="price">
          {{ price }}
        </h3>

        <p
          v-if="bibNumber"
          class="metadata"
        >
          {{ item.event.name }} {{ item.bibNumber ? '| Bib no. ' + item.bibNumber : '' }}
        </p>

        <div class="details">
          <p>
            <FontAwesomeIcon
              :icon="itemIcon"
              fixed-width
            />
            <span class="text-sm">
              {{ productSize }}{{ productDetails }}

              <template v-if="item.type === PhotoType.Single">
                <CartEditItemButton
                  :disabled="isDeleting"
                  @click="$emit('customise')"
                />
              </template>
            </span>
          </p>
        </div>

        <CartRemoveItemButton
          :deleting="isDeleting"
          @click="$emit('remove')"
        />
      </section>

      <template v-if="item.type === PhotoType.Single">
        <footer>
          <h3 class="price">
            {{ price }}
          </h3>

          <!-- Quantity Dropdown -->
          <CartDropdown
            v-if="printItem"
            :item="item"
          />

          <!-- Customise Item -->
          <CartEditItemButton
            :disabled="isDeleting"
            @click="$emit('customise')"
          />

          <!-- Remove -->
          <CartRemoveItemButton
            :deleting="isDeleting"
            @click="$emit('remove')"
          />
        </footer>
      </template>

      <template v-else>
        <footer class="max-[768px]:hidden">
          <h3 class="price">
            {{ price }}
          </h3>

          <!-- Quantity Dropdown -->
          <CartDropdown
            v-if="printItem"
            :item="item"
          />

          <!-- Remove -->
          <CartRemoveItemButton
            :deleting="isDeleting"
            @click="$emit('remove')"
          />
        </footer>
      </template>
    </article>
  </li>
</template>

<script setup>
import { computed } from 'vue';

import { CartEditItemButton, CartRemoveItemButton } from '@/components/buttons';
import { CartDropdown } from '@/components/dropdowns';
import { ProductPreview } from '@/components/orders';

import { faArrowDownToLine, faPrint } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { DeliveryType, DigitalSize, PhotoType, StyleType } from '@/const';

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
});

defineEmits(['customise', 'remove']);

const deleting = defineModel('deleting');

const isPhotoPack = computed(() => {
  return props.item && props.item.type === PhotoType.Pack;
});

const productName = computed(() => {
  return isPhotoPack.value ? PhotoType.Pack : props.item.size === DigitalSize.HighDefinition ? 'Premium Photo' : 'Standard Photo';
});

const printItem = computed(() => {
  return props.item.delivery === DeliveryType.Print;
});

const price = computed(() => {
  return `R ${props.item.price.toFixed(2)}`;
});

const bibNumber = computed(() => {
  return props.item.bibNumber ? '| Bib no. ' + props.item.bibNumber : '';
});

const itemIcon = computed(() => {
  return printItem.value ? faPrint : faArrowDownToLine;
});

const isDeleting = computed(() => {
  if (deleting.value && deleting.value.length > 0) {
    return deleting.value.includes(props.item.id);
  }
});

const productSize = computed(() => {
  const sizes = {
    'Digital Standard': 'Standard digital download',
    'Digital High Definition': 'High Definition digital download',
    'Print Standard': 'Standard Gloss Print',
    'Print Jumbo': 'Jumbo Gloss Print',
    'Print A4': 'A4 Gloss print',
    'Print A3 Canvas': 'A3 Canvas Print',
  };

  if (props.item.type === PhotoType.Single) {
    return sizes[props.item.size];
  }

  return 'Standard digital download';
});

const productDetails = computed(() => {
  return props.item.style === StyleType.Event ? ', with event details' : props.item.style === StyleType.RaceTime ? ', with race time' : '';
});

</script>
